import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Award, ShieldCheck, Sparkles, Phone, Mail, MapPin } from "lucide-react";
import api from "../lib/api";
import { FadeUp, Counter } from "./Motion";
import LeadForm from "./LeadForm";
import CallbackWidget from "./CallbackWidget";
import { useSettings } from "../lib/useSettings";
import { FAQSchema, BreadcrumbSchema } from "./SeoSchema";
import PageMeta from "./PageMeta";

/**
 * ServicePageTemplate
 * Light, editorial layout for Residential sub-service pages.
 * All copy / images / FAQs / process pulled from a single `config` prop.
 */
export default function ServicePageTemplate({ config }) {
  const settings = useSettings();
  const phoneDisplay = settings?.phone_display || "(780) 655-7604";
  const phoneE164 = settings?.phone || "+1-780-655-7604";
  const email = settings?.email || "northcrestedmonton@gmail.com";
  const [areas, setAreas] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (config.areaLinksSlug) api.get("/service-areas").then((r) => setAreas(r.data || [])).catch(() => {});
  }, [config.areaLinksSlug]);

  const {
    slug, title, italicTitle, kicker, intro,
    metaTitle, metaDescription,
    heroImage, studioImage, commercialImage,
    studioHeading, studioItalic, studioBody,
    services, process, faqs,
    commercialHeading, commercialBody,
  } = config;
  const section = config.section || { path: "/residential", label: "Residential" };
  const pagePath = `${section.path}/${slug}`;

  return (
    <div data-testid={`${slug}-page`} className="bg-[#F5F3EF] text-[#151515]">
      <PageMeta
        title={metaTitle}
        description={metaDescription}
        path={pagePath}
        image={heroImage}
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[
        { name: "Home", url: "https://northcrestlandscaping.ca/" },
        { name: section.label, url: `https://northcrestlandscaping.ca${section.path}` },
        { name: title, url: `https://northcrestlandscaping.ca${pagePath}` },
      ]} />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-16 border-b border-[#151515]/10">
        <FadeUp>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#151515]/50 mb-6">
            <Link to="/" className="hover:text-[#0B2B1F] link-underline">Home</Link>
            <span>/</span>
            <Link to={section.path} className="hover:text-[#0B2B1F] link-underline">{section.label}</Link>
            <span>/</span>
            <span className="text-[#8C1D30]">{title}</span>
          </div>
          <div className="overline text-[#8C1D30] mb-5">{kicker}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            {title.split(italicTitle)[0]}<span className="italic text-[#0B2B1F]">{italicTitle}</span>{title.split(italicTitle)[1]}
          </h1>
          <p className="text-[#151515]/70 mt-10 max-w-2xl text-lg md:text-xl font-light leading-relaxed">
            {intro}
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/estimate" data-testid={`${slug}-cta-estimate`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300">
              Get Free Estimate <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${phoneE164}`} data-testid={`${slug}-cta-call`} className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#151515]/25 hover:border-[#0B2B1F] hover:bg-[#0B2B1F] hover:text-[#F5F3EF] text-[#151515] text-sm tracking-wider uppercase transition-all duration-300">
              <Phone className="w-4 h-4" /> {phoneDisplay}
            </a>
          </div>
        </FadeUp>
      </section>

      {/* HERO IMAGE */}
      <section className="px-6 md:px-12 lg:px-16 py-12">
        <FadeUp>
          <div className="aspect-[16/8] overflow-hidden">
            <img src={heroImage} alt={`${title} — Edmonton residential project`} className="w-full h-full object-cover" />
          </div>
        </FadeUp>
      </section>

      {/* INTRO */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeUp className="lg:col-span-7">
          <div className="overline text-[#8C1D30] mb-4">Locally owned · Edmonton-based</div>
          <h2 className="font-serif-display text-3xl md:text-5xl leading-tight">
            Complete {title.toLowerCase()} across <span className="italic text-[#0B2B1F]">Edmonton & region.</span>
          </h2>
          <p className="text-[#151515]/70 mt-6 text-base md:text-lg leading-relaxed">
            NorthCrest is a locally owned and operated Edmonton landscape studio specializing in <strong className="text-[#151515]">{title.toLowerCase()} across Edmonton, St. Albert, Sherwood Park, Beaumont, Leduc, Spruce Grove, and Fort Saskatchewan.</strong>
          </p>
          <p className="text-[#151515]/70 mt-5 text-base md:text-lg leading-relaxed">
            We take pride in providing professional, architect-led services and craftsmanship for every part of your project — from initial consultation to final walk-through.
          </p>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-5 bg-[#0B2B1F] text-[#F5F3EF] p-8 md:p-10">
          <div className="font-serif-display text-6xl md:text-7xl">6+</div>
          <div className="overline text-[#D8D6D0]/70 mt-3">Years of Service</div>
          <div className="border-t border-[#F5F3EF]/15 mt-6 pt-6 space-y-3 text-sm">
            <a href={`tel:${phoneE164}`} className="flex items-center gap-3 hover:text-[#E2B85A] transition-colors">
              <Phone className="w-4 h-4 text-[#8C1D30]" /> {phoneDisplay}
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-3 hover:text-[#E2B85A] transition-colors">
              <Mail className="w-4 h-4 text-[#8C1D30]" /> {email}
            </a>
            <div className="flex items-center gap-3 text-[#D8D6D0]/80">
              <MapPin className="w-4 h-4 text-[#8C1D30]" /> Edmonton, Alberta
            </div>
          </div>
          <Link to="/estimate" className="inline-flex items-center gap-2 mt-7 px-6 py-3 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors">
            Get Estimate <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </FadeUp>
      </section>

      {/* STUDIO APPROACH (dark accent) */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-[#0B2B1F] text-[#F5F3EF]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="overline text-[#E2B85A] mb-4">The Studio Approach</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-8">
              {studioHeading} <span className="italic text-[#D8D6D0]">{studioItalic}</span>
            </h2>
            <p className="text-[#D8D6D0]/85 text-base md:text-lg leading-relaxed">
              {studioBody}
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={studioImage} alt={`${title} studio approach`} className="w-full h-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">What we offer</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight mb-6 max-w-4xl">
            Built to last. <span className="italic text-[#0B2B1F]">Designed to elevate.</span>
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
          {services.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.05}>
              <article data-testid={`${slug}-service-${i}`} className="group h-full bg-white border border-[#151515]/10 p-7 md:p-9 hover:border-[#0B2B1F] transition-colors">
                {s.img ? (
                  <div className="flex items-start gap-6">
                    <img src={s.img} alt="" className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 object-contain mt-1" loading="lazy" />
                    <div>
                      <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mb-3">{s.title}</h3>
                      <p className="text-[#151515]/70 leading-relaxed text-sm md:text-base">{s.body}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-5 mb-5">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#0B2B1F] text-[#F5F3EF] flex items-center justify-center">
                        <s.icon className="w-5 h-5" />
                      </div>
                      <h3 className="font-serif-display text-2xl md:text-3xl leading-tight flex-1">{s.title}</h3>
                    </div>
                    <p className="text-[#151515]/70 leading-relaxed text-sm md:text-base">{s.body}</p>
                  </>
                )}
              </article>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <div className="mt-12 text-center">
            <Link to="/estimate" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B2B1F] hover:bg-[#143a2d] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300">
              Get Free Estimate <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </FadeUp>
      </section>

      {/* PRICING */}
      {config.pricing && (
        <section data-testid={`${slug}-pricing`} className="px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-4">Pricing</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-12 max-w-3xl">
              Simple, honest <span className="italic text-[#0B2B1F]">rates.</span>
            </h2>
          </FadeUp>
          <div className="max-w-3xl divide-y divide-[#151515]/10 border-y border-[#151515]/10">
            {config.pricing.rows.map((r) => (
              <FadeUp key={r.label}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-6 py-6">
                  <div className="text-[#151515]/80">{r.label}</div>
                  <div className="font-serif-display text-2xl md:text-3xl text-[#0B2B1F] whitespace-nowrap">{r.value}</div>
                </div>
              </FadeUp>
            ))}
          </div>
          {config.pricing.note && (
            <p className="max-w-3xl mt-6 text-sm text-[#151515]/55 leading-relaxed">{config.pricing.note}</p>
          )}
        </section>
      )}

      {/* PROCESS */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-y border-[#151515]/10 bg-white">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Our Process</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-16 max-w-3xl">
            From idea to install in <span className="italic text-[#0B2B1F]">five steps.</span>
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
          {process.map((p, i) => (
            <FadeUp key={p.n} delay={i * 0.06}>
              <div className="border-t-2 border-[#0B2B1F] pt-6">
                <div className="font-serif-display text-4xl text-[#8C1D30]">{p.n}</div>
                <h3 className="font-serif-display text-xl mt-3">{p.title}</h3>
                <p className="text-sm text-[#151515]/65 mt-3 leading-relaxed">{p.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="px-6 md:px-12 lg:px-16 py-20 grid grid-cols-2 md:grid-cols-4 gap-10">
        <FadeUp>
          <ShieldCheck className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl">Licensed</div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">& Fully Insured</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <Award className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl"><Counter value={200} suffix="+" /></div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Projects Delivered</div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Sparkles className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl">6+ yrs</div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Edmonton Studio</div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <CheckCircle2 className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl">4.9★</div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Google Rating</div>
        </FadeUp>
      </section>

      {/* COMMERCIAL CROSS-LINK */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-[#F5F3EF] border-y border-[#151515]/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={commercialImage} alt="Edmonton commercial landscape" className="w-full h-full object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-6">
            <div className="overline text-[#8C1D30] mb-4">Commercial</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">
              {commercialHeading}
            </h2>
            <p className="text-[#151515]/70 text-base md:text-lg leading-relaxed mb-8">
              {commercialBody}
            </p>
            <Link to="/commercial" className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#0B2B1F] text-[#0B2B1F] hover:bg-[#0B2B1F] hover:text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors">
              View Commercial <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">FAQ</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight max-w-3xl mb-4">
            {title} <span className="italic text-[#0B2B1F]">questions</span>, answered.
          </h2>
          <p className="text-[#151515]/65 max-w-2xl text-base md:text-lg">
            The most common questions our clients ask. Need more info? Reach out — we&apos;re glad to talk.
          </p>
        </FadeUp>
        <div className="mt-12 divide-y divide-[#151515]/10 max-w-4xl">
          {faqs.map((f, i) => (
            <FadeUp key={f.q} delay={i * 0.04}>
              <details className="group py-6" data-testid={`${slug}-faq-${i}`}>
                <summary className="cursor-pointer list-none flex justify-between items-start gap-6">
                  <span className="font-serif-display text-xl md:text-2xl">{f.q}</span>
                  <span className="text-2xl text-[#8C1D30] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#151515]/70 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* AREAS WE SERVE */}
      {config.areaLinksSlug && areas.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-16 border-t border-[#151515]/10" data-testid={`${slug}-area-links`}>
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-4">Areas we serve</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mb-8">
              {title} across the <span className="italic text-[#0B2B1F]">Edmonton region.</span>
            </h2>
            <div className="flex flex-wrap gap-3">
              {areas.map((a) => (
                <Link
                  key={a.slug}
                  to={`/areas/${a.slug}/${config.areaLinksSlug}`}
                  data-testid={`${slug}-area-link-${a.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#151515]/15 hover:border-[#0B2B1F] hover:bg-[#0B2B1F] hover:text-[#F5F3EF] transition-colors text-sm"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#8C1D30]" />
                  {title} in {a.name}
                </Link>
              ))}
            </div>
          </FadeUp>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-[#0B2B1F] text-[#F5F3EF]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <FadeUp>
            <div className="overline text-[#E2B85A] mb-4">Get Your Free Estimate</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight mb-6">
              Start your <span className="italic text-[#D8D6D0]">{title.toLowerCase()}</span> project.
            </h2>
            <p className="text-[#D8D6D0]/80 mt-6 max-w-md text-base md:text-lg leading-relaxed">
              From the first consultation to the final walk-through, we&apos;re committed to delivering high-quality results. Schedule your <strong className="text-[#F5F3EF]">free consultation</strong> today.
            </p>
            <div className="mt-10 space-y-3 text-sm">
              <a href={`tel:${phoneE164}`} className="flex items-center gap-3 text-[#D8D6D0]/85 hover:text-[#E2B85A] transition-colors">
                <Phone className="w-4 h-4 text-[#8C1D30]" /> {phoneDisplay}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-[#D8D6D0]/85 hover:text-[#E2B85A] transition-colors">
                <Mail className="w-4 h-4 text-[#8C1D30]" /> {email}
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <LeadForm leadType={section.path === "/commercial" ? "commercial" : "residential"} source={slug} compact />
          </FadeUp>
        </div>
      </section>
      <CallbackWidget service={slug} leadType={section.path === "/commercial" ? "commercial" : "residential"} />
    </div>
  );
}
